import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus, Trash2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { AiAvatar, KIOSK_MESSAGES } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const IDLE_TIMEOUT = 180; // 3 minutes on form page

const categoryDetails = {
  disabled_blue_badge: { name: "Disabled Blue Badge", speechName: "Disabled Blue Badge", requiresOCR: true },
  adult_with_child: { name: "Adult with Child in Vehicle", speechName: "Adult with Child in Vehicle", requiresOCR: false },
  extra_wide: { name: "Extra Wide Bay", speechName: "Extra Wide Bay", requiresOCR: false },
  nhs_staff: { name: "NHS Staff", speechName: "NHS Staff", requiresOCR: false },
  military: { name: "Military / Ex-Military", speechName: "Military or Ex-Military", requiresOCR: false }
};

export default function KioskForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get category from state or URL params
  const stateCategory = location.state?.category;
  const urlCategoryId = searchParams.get("category");
  
  const [category, setCategory] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const [formData, setFormData] = useState({
    vehicle_registration: "",
    full_name: "",
    email: "",
    phone: "",
    nhs_staff_id: "",
    military_id: "",
    children: [{ date_of_birth: "" }]
  });

  const [errors, setErrors] = useState({});
  const idleTimerRef = useRef(null);

  // Idle detection - MUST be before any conditional returns
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  useEffect(() => {
    if (stateCategory) {
      setCategory(stateCategory);
    } else if (urlCategoryId && categoryDetails[urlCategoryId]) {
      setCategory({
        id: urlCategoryId,
        name: categoryDetails[urlCategoryId].name,
        requiresOCR: categoryDetails[urlCategoryId].requiresOCR
      });
    } else {
      setShouldRedirect(true);
    }
  }, [stateCategory, urlCategoryId]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate("/kiosk/category");
    }
  }, [shouldRedirect, navigate]);

  if (!category) {
    return (
      <div className="kiosk-container flex items-center justify-center min-h-screen">
        <p className="text-slate-500 text-xl">Loading...</p>
      </div>
    );
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleChildDOBChange = (index, value) => {
    const newChildren = [...formData.children];
    newChildren[index] = { date_of_birth: value };
    setFormData(prev => ({ ...prev, children: newChildren }));
  };

  const addChild = () => {
    setFormData(prev => ({
      ...prev,
      children: [...prev.children, { date_of_birth: "" }]
    }));
  };

  const removeChild = (index) => {
    if (formData.children.length > 1) {
      const newChildren = formData.children.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, children: newChildren }));
    }
  };

  const validateChildAge = (dobString) => {
    if (!dobString) return { valid: false, error: "Date of birth is required" };
    
    const dob = new Date(dobString);
    const now = new Date();
    
    // Calculate age
    let years = now.getFullYear() - dob.getFullYear();
    let months = now.getMonth() - dob.getMonth();
    
    // Adjust for birthday not yet reached
    if (now.getMonth() < dob.getMonth() || 
        (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) {
      years -= 1;
    }
    
    // Child must be UNDER 12
    if (years >= 12) {
      if (years === 12 && months <= 0) {
        return { 
          valid: false, 
          error: `Child is exactly 12 years old. Registration is only available for children under 12.`,
          years 
        };
      }
      return { 
        valid: false, 
        error: `Child is ${years} years old. Registration is only available for children under 12.`,
        years 
      };
    }
    
    return { valid: true, years, months: Math.abs(months) };
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.vehicle_registration.trim()) {
      newErrors.vehicle_registration = "Vehicle registration is required";
    }
    
    // Full name is NOT required for Blue Badge (it will be extracted from OCR)
    if (category.id !== "disabled_blue_badge" && !formData.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (category.id === "nhs_staff" && !formData.nhs_staff_id.trim()) {
      newErrors.nhs_staff_id = "NHS Staff ID is required";
    }
    if (category.id === "military" && !formData.military_id.trim()) {
      newErrors.military_id = "Military ID is required";
    }
    if (category.id === "adult_with_child") {
      const validChildren = formData.children.filter(c => c.date_of_birth);
      if (validChildren.length === 0) {
        newErrors.children = "At least one child's date of birth is required";
      } else {
        // Validate each child's age
        for (let i = 0; i < validChildren.length; i++) {
          const ageCheck = validateChildAge(validChildren[i].date_of_birth);
          if (!ageCheck.valid) {
            newErrors.children = ageCheck.error;
            break;
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validate()) {
      const registrationData = {
        ...formData,
        category: category.id,
        site_id: "site_001",
        kiosk_id: "kiosk_001"
      };

      // Clean up category-specific fields
      if (category.id !== "nhs_staff") delete registrationData.nhs_staff_id;
      if (category.id !== "military") delete registrationData.military_id;
      if (category.id !== "adult_with_child") delete registrationData.children;

      if (category.requiresOCR || category.id === "disabled_blue_badge") {
        // For Blue Badge, go to the intro/instruction screen first
        navigate("/kiosk/badge-intro", { state: { registrationData } });
      } else {
        navigate("/kiosk/processing", { state: { registrationData } });
      }
    }
  };

  return (
    <div className="kiosk-container flex flex-col min-h-screen p-6 md:p-12" data-testid="kiosk-form">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <ParkWiseLogo size="md" />
        
        {/* Progress */}
        <div className="flex items-center gap-2">
          <div className="progress-step completed">1</div>
          <div className="progress-line completed w-8"></div>
          <div className="progress-step active">2</div>
          <div className="progress-line pending w-8"></div>
          <div className="progress-step pending">3</div>
        </div>
      </div>

      {/* AI Avatar */}
      <div className="flex justify-center mb-4">
        <AiAvatar 
          message={`You are registering for ${category.speechName || category.name} parking. Please enter your vehicle registration number and personal details.`} 
          size="small"
          autoSpeak={true}
        />
      </div>

      {/* Title */}
      <div className="text-center mb-6">
        <h1 
          className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Enter Your Details
        </h1>
        <p className="text-lg text-slate-600">
          Registering for: <span className="font-semibold text-blue-600">{category.name}</span>
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 max-w-2xl mx-auto w-full">
        <div className="space-y-6">
          {/* Vehicle Registration */}
          <div className="space-y-2">
            <Label htmlFor="vehicle_registration" className="text-lg font-medium text-slate-700">
              Vehicle Registration Number *
            </Label>
            <Input
              id="vehicle_registration"
              value={formData.vehicle_registration}
              onChange={(e) => handleChange("vehicle_registration", e.target.value.toUpperCase())}
              placeholder="e.g. AB12 CDE"
              className={`h-16 text-2xl text-center font-mono uppercase tracking-wider ${
                errors.vehicle_registration ? 'border-red-500' : ''
              }`}
              data-testid="input-vehicle-registration"
            />
            {errors.vehicle_registration && (
              <p className="text-red-500 text-sm">{errors.vehicle_registration}</p>
            )}
          </div>

          {/* Full Name - Hidden for Blue Badge (extracted from OCR) */}
          {category.id !== "disabled_blue_badge" && (
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-lg font-medium text-slate-700">
                Full Name *
              </Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                placeholder="Enter your full name"
                className={`h-14 text-xl ${errors.full_name ? 'border-red-500' : ''}`}
                data-testid="input-full-name"
              />
              {errors.full_name && (
                <p className="text-red-500 text-sm">{errors.full_name}</p>
              )}
            </div>
          )}

          {/* Blue Badge Info Notice */}
          {category.id === "disabled_blue_badge" && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> Your name and badge details will be automatically extracted when you scan your Blue Badge on the next screen.
              </p>
            </div>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-lg font-medium text-slate-700">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="your@email.com"
              className={`h-14 text-xl ${errors.email ? 'border-red-500' : ''}`}
              data-testid="input-email"
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email}</p>
            )}
          </div>

          {/* Phone (optional) */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-lg font-medium text-slate-700">
              Phone Number (optional)
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="e.g. 07700 900123"
              className="h-14 text-xl"
              data-testid="input-phone"
            />
          </div>

          {/* Category-specific fields */}
          {category.id === "nhs_staff" && (
            <div className="space-y-2">
              <Label htmlFor="nhs_staff_id" className="text-lg font-medium text-slate-700">
                NHS Staff ID *
              </Label>
              <Input
                id="nhs_staff_id"
                value={formData.nhs_staff_id}
                onChange={(e) => handleChange("nhs_staff_id", e.target.value)}
                placeholder="Enter your NHS Staff ID"
                className={`h-14 text-xl ${errors.nhs_staff_id ? 'border-red-500' : ''}`}
                data-testid="input-nhs-staff-id"
              />
              {errors.nhs_staff_id && (
                <p className="text-red-500 text-sm">{errors.nhs_staff_id}</p>
              )}
            </div>
          )}

          {category.id === "military" && (
            <div className="space-y-2">
              <Label htmlFor="military_id" className="text-lg font-medium text-slate-700">
                Military / Veterans ID *
              </Label>
              <Input
                id="military_id"
                value={formData.military_id}
                onChange={(e) => handleChange("military_id", e.target.value)}
                placeholder="Enter your Military ID"
                className={`h-14 text-xl ${errors.military_id ? 'border-red-500' : ''}`}
                data-testid="input-military-id"
              />
              {errors.military_id && (
                <p className="text-red-500 text-sm">{errors.military_id}</p>
              )}
            </div>
          )}

          {category.id === "adult_with_child" && (
            <div className="space-y-4">
              <Label className="text-lg font-medium text-slate-700">
                Children's Date of Birth *
              </Label>
              {formData.children.map((child, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Input
                    type="date"
                    value={child.date_of_birth}
                    onChange={(e) => handleChildDOBChange(index, e.target.value)}
                    className="h-14 text-xl flex-1"
                    data-testid={`input-child-dob-${index}`}
                  />
                  {formData.children.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeChild(index)}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-6 h-6" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addChild}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                data-testid="add-child-button"
              >
                <Plus className="w-5 h-5" />
                Add another child
              </button>
              {errors.children && (
                <p className="text-red-500 text-sm">{errors.children}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t-2 border-slate-200">
        <button
          onClick={() => navigate("/kiosk/category")}
          className="kiosk-btn-secondary flex items-center gap-3"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
          <span>Back</span>
        </button>

        <button
          onClick={handleContinue}
          className="kiosk-btn-primary flex items-center gap-3"
          data-testid="continue-button"
        >
          <span>Continue</span>
          <ArrowRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}
